import ArticleLayout from '@/pages/article/components/ArticleLayout';

const FAQ_SCHEMA = JSON.stringify({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    { '@type': 'Question', name: 'Wie lange dauert der Versand von OOPBUY nach Deutschland?', acceptedAnswer: { '@type': 'Answer', text: 'Der Versand von China nach Deutschland dauert je nach Versandmethode zwischen 7 und 30 Tagen. Express-Versand (DHL, FedEx) benötigt 7-14 Tage, Standardversand 15-30 Tage.' } },
    { '@type': 'Question', name: 'Fallen Zollgebühren bei OOPBUY Bestellungen an?', acceptedAnswer: { '@type': 'Answer', text: 'Ja, ab einem Warenwert von 150 Euro fallen Einfuhrzölle und Einfuhrumsatzsteuer an. Unter 150 Euro ist nur die Einfuhrumsatzsteuer von 19% fällig.' } },
    { '@type': 'Question', name: 'Welche Versandmethoden bietet OOPBUY nach Deutschland an?', acceptedAnswer: { '@type': 'Answer', text: 'OOPBUY bietet DHL, FedEx, UPS, EMS und verschiedene günstigere Linien wie EU-B-Line oder DHL Tarifless Line an.' } },
    { '@type': 'Question', name: 'Wie kann ich Versandkosten sparen?', acceptedAnswer: { '@type': 'Answer', text: 'Sammle mehrere Artikel im Warehouse und versende sie zusammen. Entferne unnötige Verpackungen und wähle günstigere Versandlinien.' } },
  ],
});

export default function ShippingPage() {
  return (
    <ArticleLayout
      title="OOPBUY Shipping Deutschland 2026 – Versandkosten, Zoll & Lieferzeiten im Überblick"
      description="Alles was du über den Versand von China nach Deutschland wissen musst: Versandoptionen, Zollbestimmungen, Lieferzeiten und Spartipps für OOPBUY Nutzer."
      heroImage="https://readdy.ai/api/search-image?query=Overhead%20flat%20lay%20photograph%20of%20international%20shipping%20theme%20with%20a%20cardboard%20parcel%20box%20German%20customs%20forms%20world%20map%20snippets%20and%20a%20measuring%20tape%20on%20warm%20cream%20linen%20fabric%2C%20soft%20natural%20overhead%20lighting%2C%20organized%20editorial%20composition%2C%20warm%20neutral%20tones%20with%20subtle%20earthy%20accents&width=1200&height=500&seq=article-shipping-hero&orientation=landscape"
      heroAlt="OOPBUY Shipping Deutschland Versand"
      breadcrumbLabel="Shipping Deutschland"
      schemaMarkup={FAQ_SCHEMA}
    >
      <section className="mb-12">
        <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">
          1. OOPBUY Versand nach Deutschland – die Grundlagen
        </h2>
        <div className="text-sm md:text-base text-foreground-700 leading-relaxed space-y-4">
          <p>
            Der <strong>Versand von China nach Deutschland</strong> ist für viele Neueinsteiger die größte Hürde beim Shopping über OOPBUY. Doch mit dem richtigen Wissen wird der Prozess einfach und planbar. In diesem Guide erfährst du alles zu Versandoptionen, Kosten, Zoll und Lieferzeiten.
          </p>
          <p>
            OOPBUY arbeitet mit allen großen internationalen Versanddienstleistern zusammen und bietet sowohl <strong>Express-Optionen</strong> (DHL, FedEx, UPS) als auch <strong>günstigere Standardlinien</strong> an. Die Wahl der richtigen Versandmethode hängt von deinem Budget, der Dringlichkeit und dem Warenwert ab.
          </p>
          <p>
            <strong>Wichtig für Deutschland:</strong> Anders als innerhalb der EU fallen bei Sendungen aus China Zollgebühren und Einfuhrumsatzsteuer an. Diese sind abhängig vom Warenwert und der Produktkategorie. Wir erklären dir genau, was auf dich zukommt und wie du böse Überraschungen vermeidest.
          </p>
        </div>
      </section>

      <section className="mb-12">
        <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">
          2. Versandoptionen im Vergleich
        </h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-secondary-200">
                <th className="text-left py-3 pr-4 font-medium text-foreground-900">Versandmethode</th>
                <th className="text-left py-3 pr-4 font-medium text-foreground-900">Lieferzeit</th>
                <th className="text-left py-3 pr-4 font-medium text-foreground-900">Kosten (pro kg)</th>
                <th className="text-left py-3 pr-4 font-medium text-foreground-900">Empfohlen für</th>
              </tr>
            </thead>
            <tbody>
              {[
                ['DHL Express', '5-8 Tage', '25-35 €', 'Eilige Bestellungen, hoher Warenwert'],
                ['FedEx Priority', '5-10 Tage', '22-32 €', 'Schnelle Lieferung, Tracking'],
                ['UPS Express', '6-10 Tage', '20-30 €', 'Zuverlässige Express-Lieferung'],
                ['EU-B-Line (DHL)', '12-18 Tage', '12-18 €', 'Gutes Preis-Leistungs-Verhältnis'],
                ['DHL Tarifless Line', '14-20 Tage', '10-15 €', 'Budget-freundlich, sicher'],
                ['EMS', '10-25 Tage', '15-22 €', 'Mittelweg aus Preis und Zeit'],
              ].map((row, i) => (
                <tr key={i} className="border-b border-secondary-100">
                  <td className="py-3 pr-4 font-medium text-foreground-800">{row[0]}</td>
                  <td className="py-3 pr-4 text-foreground-600">{row[1]}</td>
                  <td className="py-3 pr-4 text-foreground-600">{row[2]}</td>
                  <td className="py-3 pr-4 text-foreground-500 text-xs">{row[3]}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-foreground-500 mt-3">
          Hinweis: Die angegebenen Preise sind Richtwerte und können je nach Paketgröße und aktuellem Wechselkurs variieren.
        </p>
      </section>

      <section className="mb-12">
        <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">
          3. Zoll und Steuern für Deutschland
        </h2>
        <div className="text-sm md:text-base text-foreground-700 leading-relaxed space-y-4">
          <div className="bg-background-100 rounded-xl p-6">
            <h3 className="font-semibold text-foreground-900 mb-3">So werden Zollgebühren berechnet:</h3>
            <ul className="space-y-3 text-sm">
              <li className="flex items-start gap-2">
                <i className="ri-checkbox-circle-line text-accent-500 text-lg flex-shrink-0 mt-0.5" />
                <span><strong>Warenwert unter 150 €:</strong> Zollfrei, nur 19% Einfuhrumsatzsteuer</span>
              </li>
              <li className="flex items-start gap-2">
                <i className="ri-checkbox-circle-line text-accent-500 text-lg flex-shrink-0 mt-0.5" />
                <span><strong>Warenwert 150 € - 1.000 €:</strong> Zoll (0-17% je nach Kategorie) + 19% EUSt</span>
              </li>
              <li className="flex items-start gap-2">
                <i className="ri-error-warning-line text-amber-500 text-lg flex-shrink-0 mt-0.5" />
                <span><strong>Schuhe & Textilien:</strong> Höhere Zollsätze (8-17%) beachten!</span>
              </li>
              <li className="flex items-start gap-2">
                <i className="ri-lightbulb-line text-amber-500 text-lg flex-shrink-0 mt-0.5" />
                <span><strong>Spartipp:</strong> Deklariere den tatsächlichen Warenwert – Unterdeklaration kann zu Strafen führen</span>
              </li>
            </ul>
          </div>
        </div>
      </section>

      <section className="mb-12">
        <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">
          4. Spartipps für den Versand
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {[
            { title: 'Warehouse nutzen', desc: 'Sammle mehrere Artikel im OOPBUY Lager und versende sie in einem Paket. Das spart deutlich Versandkosten.', icon: 'ri-archive-line' },
            { title: 'Verpackung reduzieren', desc: 'Entferne unnötige Schuhkartons und Verpackungen. Weniger Volumen = niedrigere Versandkosten.', icon: 'ri-recycle-line' },
            { title: 'Günstige Linien wählen', desc: 'DHL Tarifless Line oder EU-B-Line sind oft 40-50% günstiger als Express und trotzdem zuverlässig.', icon: 'ri-money-euro-circle-line' },
            { title: 'Gruppenbestellungen', desc: 'Bestelle mit Freunden zusammen und teile die Versandkosten auf mehrere Personen auf.', icon: 'ri-group-line' },
          ].map((item) => (
            <div key={item.title} className="bg-background-100 rounded-xl p-5 flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-accent-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                <i className={`${item.icon} text-accent-600 text-lg`} />
              </div>
              <div>
                <h3 className="text-sm font-medium text-foreground-900 mb-1">{item.title}</h3>
                <p className="text-xs text-foreground-600 leading-relaxed">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="mb-12">
        <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-6">
          5. Häufige Fragen zum Shipping
        </h2>
        <div className="space-y-3">
          {[
            { q: 'Wie lange dauert der Versand von China nach Deutschland?', a: 'Je nach Versandmethode zwischen 7 und 30 Tagen. Express-Versand (DHL, FedEx) braucht 7-14 Tage, Standardversand 15-30 Tage. Rechne zusätzlich 2-5 Tage für die Bearbeitung im OOPBUY Warehouse ein.' },
            { q: 'Fallen bei jeder Bestellung Zollgebühren an?', a: 'Bei Bestellungen unter 150 Euro Warenwert fällt kein Zoll an, nur die Einfuhrumsatzsteuer von 19%. Über 150 Euro kommen Zollsätze zwischen 0% und 17% hinzu, abhängig von der Produktkategorie. Schuhe und Textilien haben tendenziell höhere Zollsätze.' },
            { q: 'Welche Versandmethode ist am günstigsten?', a: 'DHL Tarifless Line und EU-B-Line sind die preiswertesten zuverlässigen Optionen mit Preisen von etwa 10-18 € pro Kilogramm. Sie nutzen triangle shipping über Nachbarländer und vermeiden so oft zusätzliche Zollkontrollen.' },
            { q: 'Kann ich mehrere Bestellungen zusammen versenden?', a: 'Ja, das ist sogar empfohlen! OOPBUY bietet ein Warehouse-System, in dem du Artikel bis zu 90 Tage kostenlos lagern kannst. Versende mehrere Artikel gesammelt, um Versandkosten zu sparen.' },
          ].map((faq, i) => (
            <details key={i} className="bg-background-100 rounded-xl group">
              <summary className="px-5 py-4 cursor-pointer flex items-center justify-between text-sm font-medium text-foreground-900 hover:text-accent-600 transition-colors">
                {faq.q}
                <i className="ri-add-line text-foreground-400 group-open:hidden" />
                <i className="ri-subtract-line text-foreground-400 hidden group-open:block" />
              </summary>
              <div className="px-5 pb-4 text-sm text-foreground-600 leading-relaxed">{faq.a}</div>
            </details>
          ))}
        </div>
      </section>
    </ArticleLayout>
  );
}